import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { KanbanSquare, Loader2 } from 'lucide-react'

import {
  createProductionAssignments,
  getBibleStyle,
  getProductionBudget,
  listBibleCharacters,
  listBibleCostumes,
  listBibleLocations,
  listBibleProps,
  listScriptDocuments,
  listShots,
  type ProductionAssignmentItem,
  type ProductionBudgetView,
  type ProductionDepartment,
  type ProductionMember,
} from '@/api/studio-production'
import { buildShotSceneScopeKey } from '@/features/studio/lib/canvas-scope'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import { memberDisplayName } from '@/features/studio/modals/InviteMemberModal'
import {
  ProtoBtn,
  ProtoInput,
  ProtoLabel,
  ProtoModal,
  ProtoSelect,
  ProtoTabs,
} from '@/features/studio/components/ui-kit'
import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox'

type AssignTab = 'character' | 'location' | 'prop' | 'costume' | 'style' | 'shots'

type PickItem = {
  key: string
  item_type: string
  item_public_id: string
  label: string
  group?: string
}

function itemKey(item_type: string, item_public_id: string) {
  return `${item_type}:${item_public_id}`
}

export function BulkAssignWorkModal({
  open,
  onClose,
  productionId,
  members,
  departments,
  gate,
  onAssigned,
}: {
  open: boolean
  onClose: () => void
  productionId: string
  members: ProductionMember[]
  departments: ProductionDepartment[]
  gate: { isOwner: boolean; isDeptAdmin: boolean; myDepartmentId: string | null }
  onAssigned?: () => void
}) {
  const { t } = useTranslation()
  const [tab, setTab] = useState<AssignTab>('character')
  const [items, setItems] = useState<PickItem[]>([])
  const [loadingItems, setLoadingItems] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [departmentId, setDepartmentId] = useState('')
  const [assigneeId, setAssigneeId] = useState('')
  const [quota, setQuota] = useState('500')
  const [budget, setBudget] = useState<ProductionBudgetView | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadTabItems = useCallback(async () => {
    if (!productionId || !open) return
    setLoadingItems(true)
    setError(null)
    try {
      let next: PickItem[] = []
      if (tab === 'character') {
        const res = await listBibleCharacters(productionId, { limit: 500, offset: 0 })
        next = res.items.map((c) => ({
          key: itemKey('bible_character', c.public_id),
          item_type: 'bible_character',
          item_public_id: c.public_id,
          label: c.name,
        }))
      } else if (tab === 'location') {
        const res = await listBibleLocations(productionId, { limit: 500, offset: 0 })
        next = res.items.map((c) => ({
          key: itemKey('bible_location', c.public_id),
          item_type: 'bible_location',
          item_public_id: c.public_id,
          label: c.name,
        }))
      } else if (tab === 'prop') {
        const res = await listBibleProps(productionId, { limit: 500, offset: 0 })
        next = res.items.map((c) => ({
          key: itemKey('bible_prop', c.public_id),
          item_type: 'bible_prop',
          item_public_id: c.public_id,
          label: c.name,
        }))
      } else if (tab === 'costume') {
        const res = await listBibleCostumes(productionId, { limit: 500, offset: 0 })
        next = res.items.map((c) => ({
          key: itemKey('bible_costume', c.public_id),
          item_type: 'bible_costume',
          item_public_id: c.public_id,
          label: c.name,
        }))
      } else if (tab === 'style') {
        const style = await getBibleStyle(productionId).catch(() => null)
        const title =
          [style?.art_direction, style?.color_palette].map((s) => s?.trim()).find(Boolean) ??
          t('bible.tab.style')
        next = [
          {
            key: itemKey('bible_style', 'default'),
            item_type: 'bible_style',
            item_public_id: 'default',
            label: title,
          },
        ]
      } else {
        const docs = await listScriptDocuments(productionId).catch(() => ({ items: [] }))
        const scriptId = docs.items[0]?.id
        const shots = await listShots(productionId, { limit: 500 }).catch(() => ({ items: [] }))
        const scenesByEp = new Map<number, Set<number>>()
        for (const sh of shots.items) {
          const set = scenesByEp.get(sh.episode_no) ?? new Set()
          set.add(sh.scene_no)
          scenesByEp.set(sh.episode_no, set)
        }
        const episodeNos = [...new Set(shots.items.map((s) => s.episode_no))].sort((a, b) => a - b)
        for (const ep of episodeNos) {
          next.push({
            key: `episode:${ep}`,
            item_type: '__episode_group__',
            item_public_id: String(ep),
            label: t('bulkAssign.wholeEpisode', { ep }),
            group: t('bulkAssign.episodeGroup'),
          })
          if (scriptId != null) {
            for (const sceneNo of [...(scenesByEp.get(ep) ?? [])].sort((a, b) => a - b)) {
              const sceneKey = buildShotSceneScopeKey(scriptId, ep, sceneNo)
              next.push({
                key: itemKey('shot_scene', sceneKey),
                item_type: 'shot_scene',
                item_public_id: sceneKey,
                label: t('team.assignmentSceneTitle', { ep, scene: sceneNo }),
                group: t('bulkAssign.wholeEpisode', { ep }),
              })
            }
          }
          for (const sh of shots.items.filter((s) => s.episode_no === ep)) {
            const title =
              sh.slug?.trim() || `E${String(sh.episode_no).padStart(2, '0')}-S${sh.scene_no}-${sh.shot_no}`
            next.push({
              key: itemKey('shot', sh.public_id),
              item_type: 'shot',
              item_public_id: sh.public_id,
              label: title,
              group: t('bulkAssign.wholeEpisode', { ep }),
            })
          }
        }
      }
      setItems(next)
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'bulkAssign.loadFailed'))
      setItems([])
    } finally {
      setLoadingItems(false)
    }
  }, [open, productionId, tab, t])

  useEffect(() => {
    if (!open) return
    setTab('character')
    setSelected(new Set())
    setDepartmentId('')
    setAssigneeId('')
    setQuota('500')
    setError(null)
    void getProductionBudget(productionId)
      .then(setBudget)
      .catch(() => setBudget(null))
  }, [open, productionId])

  useEffect(() => {
    if (!open) return
    setSelected(new Set())
    void loadTabItems()
  }, [open, tab, loadTabItems])

  const assigneeOptions = useMemo(() => {
    let pool = members
    if (departmentId) {
      pool = members.filter((m) => m.department_id === departmentId)
    } else if (!gate.isOwner && gate.isDeptAdmin && gate.myDepartmentId) {
      pool = members.filter((m) => m.department_id === gate.myDepartmentId)
    }
    return pool.map((m) => ({ value: String(m.user_id), label: memberDisplayName(m) }))
  }, [members, departmentId, gate])

  const availableHint = useMemo(() => {
    if (!budget) return null
    if (!gate.isOwner && gate.isDeptAdmin && gate.myDepartmentId) {
      const dept = budget.departments.find((d) => d.department_id === gate.myDepartmentId)
      return dept?.sub_pool_balance ?? 0
    }
    if (departmentId) {
      const dept = budget.departments.find((d) => d.department_id === departmentId)
      return dept?.sub_pool_balance ?? budget.pool_balance
    }
    return budget.pool_balance
  }, [budget, gate, departmentId])

  const groupedItems = useMemo(() => {
    const map = new Map<string, PickItem[]>()
    for (const it of items) {
      const g = it.group ?? ''
      const list = map.get(g) ?? []
      list.push(it)
      map.set(g, list)
    }
    return map
  }, [items])

  function toggle(key: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  function selectAllInTab() {
    const keys = items.filter((it) => it.item_type !== '__episode_group__').map((it) => it.key)
    setSelected(new Set(keys))
  }

  function selectEpisode(ep: number) {
    const prefix = t('bulkAssign.wholeEpisode', { ep })
    const keys = items.filter((it) => it.group === prefix && it.item_type !== '__episode_group__').map((it) => it.key)
    setSelected((prev) => {
      const next = new Set(prev)
      for (const k of keys) next.add(k)
      return next
    })
  }

  function resolveSelectedItems(): ProductionAssignmentItem[] {
    const out: ProductionAssignmentItem[] = []
    const seen = new Set<string>()
    for (const it of items) {
      if (!selected.has(it.key) || it.item_type === '__episode_group__') continue
      const k = itemKey(it.item_type, it.item_public_id)
      if (seen.has(k)) continue
      seen.add(k)
      out.push({ item_type: it.item_type, item_public_id: it.item_public_id })
    }
    return out
  }

  async function handleSubmit() {
    const scopeItems = resolveSelectedItems()
    if (scopeItems.length === 0) {
      setError(t('assignTask.itemsRequired'))
      return
    }
    const assigneeNum = Number(assigneeId)
    if (!assigneeNum) {
      setError(t('assignTask.assigneeRequired'))
      return
    }
    const quotaNum = Math.floor(Number(quota))
    if (!Number.isFinite(quotaNum) || quotaNum <= 0) {
      setError(t('assignTask.quotaInvalid'))
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await createProductionAssignments(productionId, {
        department_id: departmentId || undefined,
        assignments: [{ assignee_id: assigneeNum, quota: quotaNum, items: scopeItems }],
      })
      onAssigned?.()
      onClose()
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'assignTask.failed'))
    } finally {
      setSubmitting(false)
    }
  }

  function renderPickList() {
    if (loadingItems) {
      return (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-sky-400" />
        </div>
      )
    }
    if (items.length === 0) {
      return <p className="py-6 text-center text-xs text-zinc-500">{t('bulkAssign.emptyTab')}</p>
    }
    const groups = [...groupedItems.entries()]
    if (groups.length === 1 && groups[0][0] === '') {
      return (
        <ul className="max-h-56 space-y-1 overflow-y-auto rounded-lg border border-white/[0.06] p-2">
          {groups[0][1].map((it) => (
            <li key={it.key}>
              <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-white/[0.03]">
                <Checkbox checked={selected.has(it.key)} onCheckedChange={() => toggle(it.key)} />
                <span className="text-zinc-200">{it.label}</span>
              </label>
            </li>
          ))}
        </ul>
      )
    }
    return (
      <div className="max-h-56 space-y-3 overflow-y-auto rounded-lg border border-white/[0.06] p-2">
        {groups.map(([group, list]) => (
          <div key={group || 'root'}>
            {group ? (
              <div className="mb-1 flex items-center justify-between px-2">
                <span className="text-xs font-medium text-zinc-400">{group}</span>
                {tab === 'shots' ? (
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto p-0 text-[11px] text-sky-400 hover:text-sky-300"
                    onClick={() => {
                      const ep = Number(list.find((x) => x.item_type === '__episode_group__')?.item_public_id)
                      if (ep) selectEpisode(ep)
                    }}
                  >
                    {t('bulkAssign.selectEpisode')}
                  </Button>
                ) : null}
              </div>
            ) : null}
            <ul className="space-y-0.5">
              {list
                .filter((it) => it.item_type !== '__episode_group__')
                .map((it) => (
                  <li key={it.key}>
                    <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-white/[0.03]">
                      <Checkbox checked={selected.has(it.key)} onCheckedChange={() => toggle(it.key)} />
                      <span className="text-zinc-200">{it.label}</span>
                    </label>
                  </li>
                ))}
            </ul>
          </div>
        ))}
      </div>
    )
  }

  return (
    <ProtoModal
      open={open}
      onClose={onClose}
      title={t('bulkAssign.title')}
      footer={
        <>
          <ProtoBtn size="sm" variant="secondary" onClick={onClose} disabled={submitting}>
            {t('assignTask.cancel')}
          </ProtoBtn>
          <ProtoBtn size="sm" disabled={submitting || selected.size === 0} onClick={() => void handleSubmit()}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <KanbanSquare className="h-4 w-4" />}
            {t('bulkAssign.confirm', { count: resolveSelectedItems().length })}
          </ProtoBtn>
        </>
      }
    >
      {error ? <p className="mb-3 text-sm text-rose-300">{t(error, { defaultValue: error })}</p> : null}
      <div className="mb-3">
        <ProtoTabs
          tabs={[
            { id: 'character', label: t('bible.tab.characters') },
            { id: 'location', label: t('bible.tab.locations') },
            { id: 'prop', label: t('bible.tab.props') },
            { id: 'costume', label: t('bible.tab.costumes') },
            { id: 'style', label: t('bible.tab.style') },
            { id: 'shots', label: t('bulkAssign.tabShots') },
          ]}
          active={tab}
          onChange={(id) => setTab(id as AssignTab)}
        />
      </div>
      <div className="mb-2 flex justify-end">
        <ProtoBtn size="sm" variant="ghost" onClick={selectAllInTab} disabled={loadingItems || items.length === 0}>
          {t('bulkAssign.selectAllTab')}
        </ProtoBtn>
      </div>
      {renderPickList()}
      <div className="mt-4 space-y-3 border-t border-white/[0.06] pt-4">
        {gate.isOwner ? (
          <div>
            <ProtoLabel>{t('assignTask.department')}</ProtoLabel>
            <ProtoSelect className="mt-1.5" value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
              <option value="">{t('assignTask.departmentPool')}</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </ProtoSelect>
          </div>
        ) : null}
        <div>
          <ProtoLabel required>{t('assignTask.assignee')}</ProtoLabel>
          <ProtoSelect className="mt-1.5" value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)}>
            <option value="">{t('assignTask.pickMember')}</option>
            {assigneeOptions.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </ProtoSelect>
        </div>
        <div>
          <ProtoLabel required>{t('assignTask.quota')}</ProtoLabel>
          <ProtoInput type="number" min={1} className="mt-1.5" value={quota} onChange={(e) => setQuota(e.target.value)} />
          {availableHint != null ? (
            <p className="mt-1 text-[10px] text-zinc-500">{t('assignTask.availableBalance', { n: availableHint })}</p>
          ) : null}
        </div>
      </div>
    </ProtoModal>
  )
}
